// ===========================================
// COMPONENTE: RegistrarPagoDialog
// ===========================================
// Dialog para registrar pago de un vencimiento presentado

import { useState } from 'react'
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { DollarSign, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import { getImpuestoColorClasses } from '@/lib/impuestoColorResolver'
import {
  useRegistrarPagoForm,
  getFieldError,
  type RegistrarPagoFormValues,
} from './forms'
import type { RegistrarPagoData } from '@/types/vencimientos'
import { useStore } from '@tanstack/react-form'
import { FormDialogContent } from '@/components/ui/form-dialog-content'

interface VencimientoParaPago {
  id: string | null
  tipo_impuesto: string | null
  cliente_nombre: string | null
  periodo: string | null
  fecha_vencimiento: string | null
  monto_determinado?: number | null
}

interface RegistrarPagoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vencimiento: VencimientoParaPago | null
  onConfirmar: (id: string, datos: RegistrarPagoData) => Promise<void>
}

export function RegistrarPagoDialog({
  open,
  onOpenChange,
  vencimiento,
  onConfirmar,
}: RegistrarPagoDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { form } = useRegistrarPagoForm({
    onSubmit: async (value: RegistrarPagoFormValues) => {
      if (!vencimiento?.id) return

      setIsSubmitting(true)
      try {
        await onConfirmar(vencimiento.id, {
          fecha_pago: value.fecha_pago,
          monto_pagado: value.monto_pagado,
          forma_pago: value.forma_pago,
          referencia_pago: value.referencia_pago,
          observaciones: value.observaciones,
        })
        form.reset()
        onOpenChange(false)
      } catch (error) {
        console.error('Error al registrar pago:', error)
      } finally {
        setIsSubmitting(false)
      }
    },
    defaultValues: {
      monto_pagado: vencimiento?.monto_determinado != null && vencimiento.monto_determinado > 0
        ? vencimiento.monto_determinado
        : null,
    },
  })

  const isDirty = useStore(form.store, (s) => s.isDirty)

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) form.reset()
    onOpenChange(newOpen)
  }

  if (!vencimiento) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <FormDialogContent
        isDirty={isDirty}
        onDiscard={() => handleOpenChange(false)} className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-blue-600" />
            Registrar Pago
          </DialogTitle>
          <DialogDescription>
            Registrar el pago del vencimiento fiscal
          </DialogDescription>
        </DialogHeader>

        <div className="bg-muted/50 p-3 rounded-lg space-y-2">
          <div className="flex items-center gap-2">
            {vencimiento.tipo_impuesto && (
              <Badge variant="outline" className={getImpuestoColorClasses(vencimiento.tipo_impuesto)}>
                {vencimiento.tipo_impuesto}
              </Badge>
            )}
            <span className="text-sm font-medium">{vencimiento.cliente_nombre}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            <span>Periodo: {vencimiento.periodo}</span>
            {vencimiento.fecha_vencimiento && (
              <>
                <span className="mx-2">|</span>
                <span>Vencimiento: {format(new Date(vencimiento.fecha_vencimiento), 'dd/MM/yyyy')}</span>
              </>
            )}
          </div>
          {vencimiento.monto_determinado != null && vencimiento.monto_determinado > 0 && (
            <div className="text-sm font-medium text-red-600">
              Monto a pagar: ${vencimiento.monto_determinado.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-4"
        >
          <form.Field name="fecha_pago">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Fecha de Pago *</Label>
                <Input
                  id={field.name}
                  type="date"
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="monto_pagado">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Monto Pagado</Label>
                <Input
                  id={field.name}
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="0.00"
                  value={field.state.value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    field.handleChange(val === '' ? null : parseFloat(val))
                  }}
                  onBlur={field.handleBlur}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="forma_pago">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Forma de Pago</Label>
                <Input
                  id={field.name}
                  placeholder="Ej: Transferencia, VEP, Débito automático"
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="referencia_pago">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Referencia / Comprobante</Label>
                <Input
                  id={field.name}
                  placeholder="Nro. de comprobante o referencia"
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="observaciones">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Observaciones</Label>
                <Textarea
                  id={field.name}
                  placeholder="Notas adicionales..."
                  className="resize-none"
                  rows={2}
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          </form.Field>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Registrar Pago
            </Button>
          </DialogFooter>
        </form>
      </FormDialogContent>
    </Dialog>
  )
}
